import React, { useEffect, useState, useCallback } from 'react';
import { Handle, Position, useNodes, useEdges } from '@xyflow/react';
import { 
  Popconfirm,
  Modal,
  Space, 
  Card, 
  Input, 
  Select, 
  Typography, 
  Button, 
  Form, 
  message, 
  InputNumber, 
  Switch, 
  Table, 
  Dropdown, 
  Menu,
  Tabs,
  Tag,
  Tooltip,
  Alert,
  Collapse,
  Checkbox
} from 'antd';
import { 
  DeleteOutlined, 
  PlusOutlined, 
  DownOutlined, 
  SendOutlined, 
  InfoCircleOutlined, 
  ReloadOutlined,
  SaveOutlined,
  LinkOutlined,
  FileTextOutlined,
  SettingOutlined,
  FieldTimeOutlined,
  DatabaseOutlined,
  CodeOutlined,
  CloudOutlined,
  CopyOutlined,
  CaretRightOutlined,
  DesktopOutlined,
  CrownOutlined
} from '@ant-design/icons';
import { Icon } from '@iconify/react';
import { adminInstance } from '../api/axiosConfig';
import config from '../../config';
import NodeInputHandler from '../common/NodeInputHandler';
import GenericNodeContent from '../common/GenericNodeContent';
import { handleGoogleAPIError, GoogleIntegrationStatus } from '../utils/handleGoogleAPIError';
import { isOutputTypeAllowed } from '../../config/nodeConfig';

const { Option } = Select;
const { Paragraph, Text } = Typography;
const { TabPane } = Tabs;
const { Panel } = Collapse;

const OutputNode = ({ id, data, isConnectable, workflowId  }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [existingTables, setExistingTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(data.selectedTable || '');
  const [newTableName, setNewTableName] = useState('');
  const [form] = Form.useForm();
  const [webhookUrl, setWebhookUrl] = useState(data.webhookUrl || '');
  const [isTestingWebhook, setIsTestingWebhook] = useState(false);
  const [nodeName, setNodeName] = useState(data.nodeName || `Output ${id}`);
  const [displayOutput, setDisplayOutput] = useState(data.displayOutput || "Output will be displayed here after execution");
  const [outputType, setOutputType] = useState(data.outputType || 'display');
  const [workflowIdState, setWorkflowIdState] = useState(data.workflowId || workflowId);
  const [isNewTable, setIsNewTable] = useState(false);
  const [columns, setColumns] = useState(data.columns || []);
  const [isColumnModalVisible, setIsColumnModalVisible] = useState(false);
  const [newColumn, setNewColumn] = useState({ name: '', type: 'text', mapping: '' });
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [email, setEmail] = useState(data.email || { to: '', subject: '', body: '' });
  const nodes = useNodes();
  const [webhookFields, setWebhookFields] = useState(data.webhookFields || [{ key: '', value: '' }]);
  const [isInfoModalVisible, setIsInfoModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('output');
  const [delayEnabled, setDelayEnabled] = useState(data.delayEnabled || false);
  const [delayDuration, setDelayDuration] = useState(data.delayDuration || 1000);
  const [googleSheets, setGoogleSheets] = useState([]);
  const [selectedSpreadsheet, setSelectedSpreadsheet] = useState(data.selectedSpreadsheet || '');
  const [sheetTabs, setSheetTabs] = useState([]);
  const [selectedSheetTab, setSelectedSheetTab] = useState(data.selectedSheetTab || '');
  const [sheetColumns, setSheetColumns] = useState([]);
  const [columnMappings, setColumnMappings] = useState(data.columnMappings || {});
  const [isLoadingSheets, setIsLoadingSheets] = useState(false);
  const [isLoadingTabs, setIsLoadingTabs] = useState(false);
  const [isLoadingColumns, setIsLoadingColumns] = useState(false);
  const [driveFolders, setDriveFolders] = useState([]);
  const [selectedDriveFolder, setSelectedDriveFolder] = useState(data.selectedDriveFolder || 'root');
  const [isFetchingFolders, setIsFetchingFolders] = useState(false);
  const [docTitle, setDocTitle] = useState(data.docTitle || '');
  const [driveContent, setDriveContent] = useState(data.driveContent || '');
  const [sharingLevel, setSharingLevel] = useState(data.sharingLevel || 'private');
  const [delayValue, setDelayValue] = useState(data.delayValue || 1);
  const [delayUnit, setDelayUnit] = useState(data.delayUnit || 'minutes');
  const [webhookKeys, setWebhookKeys] = useState(data.webhookKeys || []);
  const [isWebhookKeyModalVisible, setIsWebhookKeyModalVisible] = useState(false);
  const [newWebhookKey, setNewWebhookKey] = useState('');
  const [isExplanationModalVisible, setIsExplanationModalVisible] = useState(false);
  const [driveFileName, setDriveFileName] = useState(data.driveFileName || '');
  const [driveFileFormat, setDriveFileFormat] = useState(data.driveFileFormat || 'txt');
  const [driveItemType, setDriveItemType] = useState(data.driveItemType || '');
  const [spreadsheets, setSpreadsheets] = useState([]);
  const [driveItems, setDriveItems] = useState({ folders: [] });
  
  // Shortcode builder state
  const [showShortcodeBuilder, setShowShortcodeBuilder] = useState(false);
  const [shortcodeParams, setShortcodeParams] = useState(data.shortcodeParams || {
    loading: 'previous',
    loading_text: 'Processing your submission...',
    session_type: 'auto',
    session_id: '',
    refresh_interval: '5000',
    show_timestamp: false,
    format: 'auto',
    theme: 'default',
    error_display: 'inline',
    clear_on_refresh: false,
    stale_after: '20',
    stop_on_success: true
  });

  // Parameter descriptions for tooltips
  const parameterDescriptions = {
    loading: 'How to display loading state while waiting for results',
    loading_text: 'Custom message to show when loading (only with custom loading type)',
    session_type: 'Controls how outputs are shared between users',
    session_id: 'Manually set a session ID for custom session management',
    refresh_interval: 'How often to check for new results (milliseconds)',
    show_timestamp: 'Display when the output was last updated',
    format: 'How to format the output display',
    theme: 'Visual style for the output display',
    error_display: 'How to show errors to users',
    clear_on_refresh: 'Remove old results when the page is refreshed',
    stale_after: 'Consider data stale after this many seconds',
    stop_on_success: 'Stop checking for updates after receiving new data'
  };

  const getOutputTypeInfo = (type) => {
    const typeConfig = {
      display: { icon: <DesktopOutlined />, tag: 'Display Only' },
      database: { icon: <DatabaseOutlined />, tag: 'Save to Database' },
      html: { icon: <CodeOutlined />, tag: 'Shortcode' },
      webhook: { icon: <LinkOutlined />, tag: 'Send to Webhook' },
      sheet: { icon: <Icon icon="mdi:google-spreadsheet" style={{ fontSize: '16px' }} />, tag: 'Google Sheet' },
      document: { icon: <Icon icon="mdi:google-drive" style={{ fontSize: '16px' }} />, tag: 'Google Doc' }
    };
    return typeConfig[type] || { icon: <FileTextOutlined />, tag: 'Unknown' };
  };

  // Update node data whenever shortcode params change
  useEffect(() => {
    if (data.updateNodeData) {
      data.updateNodeData(id, { shortcodeParams });
    }
  }, [shortcodeParams]);

  useEffect(() => {
    if (workflowId) {
      setWorkflowIdState(workflowId);
    }
  }, [workflowId]);

  useEffect(() => {
    if (data.workflowId) {
      setWorkflowIdState(data.workflowId);
    }
  }, [data.workflowId]);

  useEffect(() => {
    if (outputType === 'sheet') {
      fetchGoogleSheets();
    } else if (outputType === 'document') {
      fetchGoogleDriveFolders();
    }
  }, [outputType]);

  useEffect(() => {
    if (selectedSpreadsheet) {
      fetchSheetTabs(selectedSpreadsheet);
    }
  }, [selectedSpreadsheet]);

  useEffect(() => {
    if (selectedSpreadsheet && selectedSheetTab) {
      fetchSheetColumns();
    }
  }, [selectedSpreadsheet, selectedSheetTab]);

  useEffect(() => {
    fetchExistingTables();
  }, []);

  useEffect(() => {
    if (selectedTable && !isNewTable) {
      fetchTableColumns(selectedTable);
    }
  }, [selectedTable, isNewTable]);

  useEffect(() => {
    setDisplayOutput(data.displayOutput || "Output will be displayed here after execution");
  }, [data.displayOutput]);

  useEffect(() => {
    data.updateNodeData(id, {
      nodeName,
      outputType,
      selectedTable,
      webhookUrl,
      displayOutput,
      delayEnabled,
      delayValue,
      delayUnit,
      columns,
      webhookKeys,
      workflowId: workflowIdState,
      selectedSpreadsheet,
      selectedSheetTab,
      columnMappings,
      driveItemType,
      selectedDriveFolder,
      docTitle,
      driveContent,
      sharingLevel,
      email,
      delayDuration,
      webhookFields
    });
  }, [nodeName, outputType, selectedTable, webhookUrl, displayOutput, delayEnabled, 
      delayValue, delayUnit, columns, workflowId, webhookKeys, selectedSpreadsheet, 
      selectedSheetTab, columnMappings, driveItemType, selectedDriveFolder, docTitle, 
      driveContent, sharingLevel, email, delayDuration, webhookFields]);

  // Generate shortcode based on current parameters
  const generateShortcode = () => {
    if (!workflowIdState) return '[wp_ai_workflows_output id="YOUR-WORKFLOW-ID"]';
    
    let shortcode = `[wp_ai_workflows_output id="${workflowIdState}"`;
    
    // Add non-default parameters
    Object.entries(shortcodeParams).forEach(([key, value]) => {
      // Skip empty values and defaults
      if (value === '' || value === null) return;
      
      // Check against defaults
      const isDefault = (
        (key === 'loading' && value === 'previous') ||
        (key === 'loading_text' && value === 'Processing your submission...') ||
        (key === 'session_type' && value === 'auto') ||
        (key === 'refresh_interval' && value === '5000') ||
        (key === 'show_timestamp' && value === false) ||
        (key === 'format' && value === 'auto') ||
        (key === 'theme' && value === 'default') ||
        (key === 'error_display' && value === 'inline') ||
        (key === 'clear_on_refresh' && value === false) ||
        (key === 'stale_after' && value === '20') ||
        (key === 'stop_on_success' && value === true)
      );
      
      if (!isDefault) {
        const stringValue = typeof value === 'boolean' ? (value ? 'true' : 'false') : value;
        shortcode += ` ${key}="${stringValue}"`;
      }
    });
    
    shortcode += ']';
    return shortcode;
  };

  const updateShortcodeParam = (key, value) => {
    setShortcodeParams(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const fetchExistingTables = async () => {
    if (data.isViewer) {
      return;
    }

    try {
      const response = await adminInstance.get('/tables');
      setExistingTables(['Create New Table', ...response.data]);
    } catch (error) {
      console.error('Error fetching existing tables:', error);
      message.error('Failed to fetch existing tables');
    }
  };

  const fetchTableColumns = async (table) => {
    if (data.isViewer) {
      return;
    }

    try {
      const response = await adminInstance.get(`/table-structure?table=${table}`);
      setColumns(response.data.map(col => ({
        name: col.name,
        type: col.type,
        mapping: data.columns?.find(c => c.name === col.name)?.mapping || ''
      })).filter(col => !['id', 'created_at'].includes(col.name)));
    } catch (error) {
      console.error('Error fetching table structure:', error);
      message.error('Failed to fetch table structure');
    }
  };

  const handleTableSelection = (value) => {
    if (value === 'Create New Table') {
      setIsNewTable(true);
      setSelectedTable('');
      setColumns([]);
    } else {
      setIsNewTable(false);
      setSelectedTable(value);
    }
  };

  const handleCreateNewTable = async () => {
    try {
      await form.validateFields();
      const response = await adminInstance.post('/tables', { 
        tableName: newTableName,
        columns: columns
      });
      if (response.data.success) {
        setExistingTables([...existingTables, response.data.tableName]);
        setSelectedTable(response.data.tableName);
        setIsNewTable(false);
        form.resetFields();
        setIsModalVisible(false);
        message.success('New table created successfully');
      }
    } catch (error) {
      console.error('Error creating new table:', error);
      message.error('Failed to create new table');
    }
  };

  const handleColumnAdd = (values) => {
    const newColumn = {
      name: values.name,
      type: values.type,
      mapping: ''
    };
    setColumns([...columns, newColumn]);
    setIsColumnModalVisible(false);
  };

  const handleColumnDelete = (index) => {
    const newColumns = columns.filter((_, i) => i !== index);
    setColumns(newColumns);
  };

  const handleWebhookUrlChange = (e) => {
    setWebhookUrl(e.target.value);
  };

  const handleTestWebhook = async () => {
    setIsTestingWebhook(true);
    try {
      const response = await adminInstance.post(`${config.apiSettings.root}wp-ai-workflows/v1/webhook-config/test`, {
        url: webhookUrl,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.data.status >= 200 && response.data.status < 300) {
        message.success('Webhook test successful!');
      } else {
        message.warning(`Webhook test received a non-200 response: ${response.data.status}`);
      }
    } catch (error) {
      console.error('Error testing webhook:', error);
      message.error('Failed to test webhook');
    } finally {
      setIsTestingWebhook(false);
    }
  };

  const handleWebhookKeyAdd = () => {
    if (newWebhookKey) {
      setWebhookKeys([...webhookKeys, { key: newWebhookKey, mapping: '' }]);
      setNewWebhookKey('');
      setIsWebhookKeyModalVisible(false);
    }
  };

  const handleWebhookKeyDelete = (keyToDelete) => {
    setWebhookKeys(webhookKeys.filter(k => k.key !== keyToDelete));
  };

  const fetchGoogleSheets = async () => {
    if (data.isViewer) {
      return;
    }

    try {
      const response = await adminInstance.get('/google-sheets');
      setSpreadsheets(response.data);
    } catch (error) {
      if (!handleGoogleAPIError(error)) {
        message.error('Failed to fetch Google Sheets');
      }
      setSpreadsheets([]);
    }
  };

  const fetchSheetTabs = async (sheetId) => {
    if (data.isViewer) {
      return;
    }

    try {
      const response = await adminInstance.get(`/google-sheets/${sheetId}/tabs`);
      setSheetTabs(response.data);
    } catch (error) {
      if (!handleGoogleAPIError(error)) {
        message.error('Failed to fetch sheet tabs');
      }
      setSheetTabs([]);
    }
  };

  const fetchSheetColumns = async () => {
    if (data.isViewer) {
      return;
    }

    console.log('Fetching sheet columns:', { selectedSpreadsheet, selectedSheetTab });
    if (!selectedSpreadsheet || selectedSheetTab === undefined || selectedSheetTab === null) {
      message.warning('Please select both a spreadsheet and a sheet tab first.');
      return;
    }
  
    setIsLoadingColumns(true);
    try {
      const response = await adminInstance.get(`/google-sheets/${selectedSpreadsheet}/tabs/${selectedSheetTab}/columns`);
      console.log('Sheet columns response:', response.data);
      setSheetColumns(response.data);
      // Initialize column mappings for new columns
      const newMappings = {};
      response.data.forEach(column => {
        if (!columnMappings[column]) {
          newMappings[column] = '';
        }
      });
      setColumnMappings(prev => ({ ...prev, ...newMappings }));
      message.success('Column structure loaded successfully');
    } catch (error) {
      console.error('Error fetching sheet columns:', error);
      if (error.response && error.response.data && error.response.data.message) {
        message.error(`Failed to fetch sheet columns: ${error.response.data.message}`);
      } else {
        message.error('Failed to fetch sheet columns');
      }
    } finally {
      setIsLoadingColumns(false);
    }
  };

  const handleSpreadsheetChange = (value) => {
    setSelectedSpreadsheet(value);
    setSelectedSheetTab(''); // Reset sheet tab when spreadsheet changes
    setSheetColumns([]); // Clear columns when spreadsheet changes
    data.updateNodeData(id, { selectedSpreadsheet: value, selectedSheetTab: '', columnMappings: {} });
  };

  const handleSheetTabChange = (value) => {
    setSelectedSheetTab(value);
    setSheetColumns([]); // Clear columns when sheet tab changes
    data.updateNodeData(id, { selectedSheetTab: value, columnMappings: {} });
  };

  const fetchGoogleDriveFolders = async () => {
    if (data.isViewer) {
      return;
    }
    
    setIsFetchingFolders(true);
    try {
        const response = await adminInstance.get('/google-drive-folders');
        if (response.data && Array.isArray(response.data)) {
            setDriveItems({ folders: response.data });
        }
    } catch (error) {
        if (!handleGoogleAPIError(error)) {
            message.error('Failed to fetch Google Drive folders');
        }
        setDriveItems({ folders: [] });
    } finally {
        setIsFetchingFolders(false);
    }
  };

  const handleEmailSend = async () => {
    if (!email.to || !email.subject || !email.body) {
      message.error('Please fill in all email fields');
      return;
    }

    setIsSendingEmail(true);
    try {
      const emailData = {
        ...email,
        subject: replaceInputTags(email.subject),
        body: replaceInputTags(email.body)
      };

      const response = await adminInstance.post('/send-email', emailData);
      if (response.data.success) {
        message.success('Test email sent successfully!');
      } else {
        message.error(response.data.message || 'Failed to send test email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      message.error('Failed to send test email');
    } finally {
      setIsSendingEmail(false);
    }
  };

  const replaceInputTags = (content) => {
    // This is a placeholder function for demonstration
    // In production, this would replace input tags with actual node data
    return content;
  };

  // Unified mapping handler
  const handleUnifiedMapping = (fieldName, value, type) => {
    switch (type) {
      case 'save':
        const newColumns = columns.map(col => 
          col.name === fieldName ? { ...col, mapping: value } : col
        );
        setColumns(newColumns);
        break;
        
      case 'googleSheets':
        setColumnMappings(prev => ({
          ...prev,
          [fieldName]: value
        }));
        break;
        
      case 'webhook':
        setWebhookKeys(prev => 
          prev.map(k => k.key === fieldName ? { ...k, mapping: value } : k)
        );
        break;
    }
  };

  const renderGoogleSheetsOutput = () => (
    <>
      {(!spreadsheets || spreadsheets.length === 0) && (
        <GoogleIntegrationStatus>
          Configure Google Sheets integration to use this output
        </GoogleIntegrationStatus>
      )}
      <Select
        style={{ width: '100%', marginBottom: 8 }}
        value={selectedSpreadsheet}
        onChange={handleSpreadsheetChange}
        placeholder="Select a spreadsheet"
        className="nodrag"
        showSearch
        optionFilterProp="children"
        filterOption={(input, option) =>
          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
      >
        {spreadsheets.map(sheet => (
          <Option key={sheet.id} value={sheet.id}>{sheet.name}</Option>
        ))}
      </Select>
      <Select
        style={{ width: '100%', marginBottom: 8 }}
        value={selectedSheetTab}
        onChange={handleSheetTabChange}
        placeholder="Select a sheet tab"
        disabled={!selectedSpreadsheet}
        className="nodrag"
        showSearch
        optionFilterProp="children"
        filterOption={(input, option) =>
          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
      >
        {sheetTabs.map(tab => (
          <Option key={tab.id} value={tab.id}>{tab.name}</Option>
        ))}
      </Select>
      <Button 
        onClick={fetchSheetColumns} 
        loading={isLoadingColumns}
        style={{ marginBottom: 8 }}
        icon={<ReloadOutlined />}
        disabled={!selectedSpreadsheet || !selectedSheetTab}
      >
        Load Column Structure
      </Button>
      <Table
        dataSource={sheetColumns.map(column => ({ column, mapping: columnMappings[column] || '' }))}
        columns={[
          {
            title: 'Column',
            dataIndex: 'column',
            key: 'column',
          },
          {
            title: 'Mapping',
            dataIndex: 'mapping',
            key: 'mapping',
            render: (text, record) => (
              <Input
                value={text}
                onChange={(e) => handleUnifiedMapping(record.column, e.target.value, 'googleSheets')}
                placeholder="Select input or enter value"
                className="nodrag"
                addonAfter={
                  <NodeInputHandler
                    nodeId={id}
                    onInsert={(tag) => handleUnifiedMapping(record.column, tag, 'googleSheets')}
                    showInputTag={false}
                  />
                }
              />
            )
          },
        ]}
        pagination={false}
        size="small"
      />
    </>
  );

  const renderGoogleDriveOutput = () => (
    <>
      {(!driveItems.folders || !driveItems.folders.length) && (
        <GoogleIntegrationStatus>
          Configure Google Drive integration to use this output
        </GoogleIntegrationStatus>
      )}
      <Form.Item 
        label={
          <Space>
            <span>Select Folder</span>
            <Tooltip title="Search and select a folder to save your file">
              <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
            </Tooltip>
          </Space>
        }
      >
        <Select
          style={{ width: '100%', marginBottom: 8 }}
          value={selectedDriveFolder}
          onChange={(value) => {
            setSelectedDriveFolder(value);
            data.updateNodeData(id, { selectedDriveFolder: value });
          }}
          placeholder={isFetchingFolders ? "Loading folders..." : "Select a folder"}
          className="nodrag"
          loading={isFetchingFolders}
          showSearch
          optionFilterProp="children"
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          <Option value="root">My Drive (Root)</Option>
          {driveItems.folders.map(folder => (
            <Option key={folder.id} value={folder.id}>{folder.name}</Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item label="Document Title">
        <NodeInputHandler
          nodeId={id}
          value={docTitle}
          onChange={(newData) => {
            setDocTitle(newData.content);
            data.updateNodeData(id, { docTitle: newData.content });
          }}
          placeholder="Enter document title or use input tags"
          nodes={nodes}
        />
      </Form.Item>

      <Form.Item label="Document Content">
        <NodeInputHandler
          nodeId={id}
          value={driveContent}
          onChange={(newData) => {
            setDriveContent(newData.content);
            data.updateNodeData(id, { driveContent: newData.content });
          }}
          placeholder="Enter document content or use input tags"
          nodes={nodes}
        />
      </Form.Item>

      <Form.Item label="Sharing Settings">
        <Select
          style={{ width: '100%', marginBottom: 8 }}
          value={sharingLevel}
          onChange={(value) => {
            setSharingLevel(value);
            data.updateNodeData(id, { sharingLevel: value });
          }}
          className="nodrag"
        >
          <Option value="private">Private (Only you)</Option>
          <Option value="anyone_read">Anyone with link can view</Option>
          <Option value="anyone_write">Anyone with link can edit</Option>
        </Select>
      </Form.Item>
    </>
  );

  return (
    <>
      <Card 
        title={
          <Space>
            {getOutputTypeInfo(outputType).icon}
            <span>Output</span>
            <Tag color="blue">{getOutputTypeInfo(outputType).tag}</Tag>
          </Space>
        }
        style={{ width: 380 }}
        extra={
          <Space>
            <Tooltip title={delayEnabled ? "Output Delayed" : "Add Delay"}>
              <Button
                type={delayEnabled ? "primary" : "text"}
                icon={<FieldTimeOutlined />}
                onClick={() => {
                  setDelayEnabled(!delayEnabled);
                  if (!delayEnabled) {
                    setActiveTab('delay');
                  }
                }}
              />
            </Tooltip>
            <Popconfirm
              title="Delete Node"
              description="Are you sure you want to delete this node?"
              onConfirm={() => data.onDelete(id)}
              okText="Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            > 
              <Button icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        }
      >
        <Handle type="target" position={Position.Left} isConnectable={isConnectable} />
        
        <Input
          placeholder="Node Name"
          value={nodeName}
          onChange={(e) => setNodeName(e.target.value)}
          style={{ marginBottom: 16 }}
          prefix={<InfoCircleOutlined style={{ color: '#bfbfbf' }} />}
          className="nodrag"
        />
  
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Output" key="output">
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Space align="center" style={{ marginBottom: 4 }}>
                  <Text strong>Output Type</Text>
                  <Tooltip title="Select how to handle the output">
                    <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
                  </Tooltip>
                </Space>
  
  
                <Select
                    style={{ width: '100%', marginBottom: 16 }}
                    value={outputType}
                    onChange={(value) => {
                      const isLite = process.env.REACT_APP_VERSION === 'lite';
                      if (!isLite || isOutputTypeAllowed(value)) {
                        setOutputType(value);
                        data.updateNodeData(id, { outputType: value });
                      }
                    }}
                    className="nodrag"
                  >
                    {/* LITE ALLOWED OUTPUT TYPES */}
                    <Option value="display">
                      <Space>
                        <FileTextOutlined />
                        <span>Display Output</span>
                      </Space>
                    </Option>
                    
                    <Option value="save">
                      <Space>
                        <DatabaseOutlined />
                        <span>Save to Database</span>
                      </Space>
                    </Option>
                    
                    <Option value="html">
                      <Space>
                        <CodeOutlined />
                        <span>Generate Shortcode</span>
                      </Space>
                    </Option>

                    {/* PRO-ONLY OUTPUT TYPES */}
                    <Option 
                      value="webhook" 
                      disabled={process.env.REACT_APP_VERSION === 'lite'}
                    >
                      <Space>
                        <LinkOutlined />
                        <span>Send to Webhook</span>
                        {process.env.REACT_APP_VERSION === 'lite' && (
                          <span style={{ color: '#1890ff', fontSize: '12px', marginLeft: '4px' }}>🔒 Pro</span>
                        )}
                      </Space>
                    </Option>
                    
                    <Option 
                      value="googleSheets" 
                      disabled={process.env.REACT_APP_VERSION === 'lite'}
                    >
                      <Space>
                        <Icon icon="mdi:google-spreadsheet" />
                        <span>Save to Google Sheets</span>
                        {process.env.REACT_APP_VERSION === 'lite' && (
                          <span style={{ color: '#1890ff', fontSize: '12px', marginLeft: '4px' }}>🔒 Pro</span>
                        )}
                      </Space>
                    </Option>
                    
                    <Option 
                      value="googleDrive" 
                      disabled={process.env.REACT_APP_VERSION === 'lite'}
                    >
                      <Space>
                        <Icon icon="mdi:google-drive" />
                        <span>Save to Google Drive</span>
                        {process.env.REACT_APP_VERSION === 'lite' && (
                          <span style={{ color: '#1890ff', fontSize: '12px', marginLeft: '4px' }}>🔒 Pro</span>
                        )}
                      </Space>
                    </Option>
                  </Select>

                  {/* ADD UPGRADE MESSAGES FOR PRO-ONLY OUTPUT TYPES */}
                  {outputType === 'webhook' && process.env.REACT_APP_VERSION === 'lite' && (
                    <div style={{ 
                      background: '#f0f8ff', 
                      padding: 16, 
                      borderRadius: 6,
                      border: '1px dashed #1890ff',
                      textAlign: 'center',
                      marginBottom: 16
                    }}>
                      <CrownOutlined style={{ fontSize: 24, color: '#1890ff', marginBottom: 8 }} />
                      <div style={{ marginBottom: 8, fontWeight: 'bold' }}>
                        Webhook Configuration
                      </div>
                      <div style={{ marginBottom: 12, color: '#666' }}>
                        🔒 Send data to external webhooks with Pro version
                      </div>
                      <Button 
                        type="primary" 
                        size="small"
                        onClick={() => window.open('https://wpaiworkflowautomation.com/pricing?utm_source=freedownload&utm_medium=lite', '_blank')}
                      >
                        Upgrade to Pro
                      </Button>
                    </div>
                  )}

                  {outputType === 'googleSheets' && process.env.REACT_APP_VERSION === 'lite' && (
                    <div style={{ 
                      background: '#f0f8ff', 
                      padding: 16, 
                      borderRadius: 6,
                      border: '1px dashed #1890ff',
                      textAlign: 'center',
                      marginBottom: 16
                    }}>
                      <CrownOutlined style={{ fontSize: 24, color: '#1890ff', marginBottom: 8 }} />
                      <div style={{ marginBottom: 8, fontWeight: 'bold' }}>
                        Google Sheets Integration
                      </div>
                      <div style={{ marginBottom: 12, color: '#666' }}>
                        🔒 Save workflow outputs directly to Google Sheets with Pro
                      </div>
                      <Button 
                        type="primary" 
                        size="small"
                        onClick={() => window.open('https://wpaiworkflowautomation.com/pricing?utm_source=freedownload&utm_medium=lite', '_blank')}
                      >
                        Upgrade to Pro
                      </Button>
                    </div>
                  )}

                  {outputType === 'googleDrive' && process.env.REACT_APP_VERSION === 'lite' && (
                    <div style={{ 
                      background: '#f0f8ff', 
                      padding: 16, 
                      borderRadius: 6,
                      border: '1px dashed #1890ff',
                      textAlign: 'center',
                      marginBottom: 16
                    }}>
                      <CrownOutlined style={{ fontSize: 24, color: '#1890ff', marginBottom: 8 }} />
                      <div style={{ marginBottom: 8, fontWeight: 'bold' }}>
                        Google Drive Integration
                      </div>
                      <div style={{ marginBottom: 12, color: '#666' }}>
                        🔒 Save files and documents to Google Drive with Pro
                      </div>
                      <Button 
                        type="primary" 
                        size="small"
                        onClick={() => window.open('https://wpaiworkflowautomation.com/pricing?utm_source=freedownload&utm_medium=lite', '_blank')}
                      >
                        Upgrade to Pro
                      </Button>
                    </div>
                  )}
              </div>

              {/* Display Output Type Content */}
              {outputType === 'display' && (
                <NodeInputHandler
                  value={displayOutput}
                  onChange={(newData) => {
                    setDisplayOutput(newData.content);
                    data.updateNodeData(id, { displayOutput: newData.content });
                  }}
                  placeholder="Output will be displayed here after execution"
                  nodes={nodes}
                />
              )}

              {/* Enhanced Shortcode Output Type Content */}
              {outputType === 'html' && (
                <div style={{ 
                  background: '#f5f5f5', 
                  padding: 12, 
                  borderRadius: 6 
                }}>
                  {workflowIdState ? (
                    <>
                      {/* Basic shortcode section */}
                      <div style={{ marginBottom: 16 }}>
                        <Text strong>Basic Shortcode:</Text>
                        <Input.TextArea
                          value={`[wp_ai_workflows_output id="${workflowIdState}"]`}
                          readOnly
                          style={{ marginTop: 8, marginBottom: 8 }}
                          autoSize={{ minRows: 1, maxRows: 3 }}
                        />
                        <Space>
                          <Button 
                            onClick={() => {
                              navigator.clipboard.writeText(`[wp_ai_workflows_output id="${workflowIdState}"]`);
                              message.success('Basic shortcode copied!');
                            }}
                            icon={<CopyOutlined />}
                            size="small"
                          >
                            Copy Basic
                          </Button>
                          <Button 
                            onClick={() => setShowShortcodeBuilder(!showShortcodeBuilder)}
                            icon={<SettingOutlined />}
                            type={showShortcodeBuilder ? "primary" : "default"}
                            size="small"
                          >
                            {showShortcodeBuilder ? 'Hide' : 'Show'} Advanced Options
                          </Button>
                        </Space>
                      </div>

                      {/* Advanced shortcode builder */}
                      {showShortcodeBuilder && (
                        <div style={{ 
                          marginTop: 16, 
                          padding: 16, 
                          background: '#fff', 
                          borderRadius: 8,
                          border: '1px solid #e8e8e8'
                        }}>
                          <Text strong style={{ marginBottom: 12, display: 'block' }}>
                            Shortcode Configuration
                          </Text>
                          
                          <Collapse 
                            ghost
                            expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                            defaultActiveKey={['display']}
                          >
                            {/* Display Settings */}
                            <Panel header="Display Settings" key="display">
                              <Space direction="vertical" style={{ width: '100%' }}>
                                <div>
                                  <Text>Loading State:</Text>
                                  <Tooltip title={parameterDescriptions.loading}>
                                    <InfoCircleOutlined style={{ marginLeft: 4, color: '#8c8c8c' }} />
                                  </Tooltip>
                                  <Select
                                    style={{ width: '100%', marginTop: 4 }}
                                    value={shortcodeParams.loading}
                                    onChange={(value) => updateShortcodeParam('loading', value)}
                                    size="small"
                                    className="nodrag"
                                  >
                                    <Option value="previous">Show Previous Result</Option>
                                    <Option value="spinner">Loading Spinner</Option>
                                    <Option value="skeleton">Skeleton Loader</Option>
                                    <Option value="custom">Custom Message</Option>
                                  </Select>
                                </div>

                                {shortcodeParams.loading === 'custom' && (
                                  <div>
                                    <Text>Loading Text:</Text>
                                    <Input
                                      value={shortcodeParams.loading_text}
                                      onChange={(e) => updateShortcodeParam('loading_text', e.target.value)}
                                      placeholder="Enter custom loading message"
                                      size="small"
                                      style={{ marginTop: 4 }}
                                    />
                                  </div>
                                )}

                                <div>
                                  <Text>Output Format:</Text>
                                  <Tooltip title={parameterDescriptions.format}>
                                    <InfoCircleOutlined style={{ marginLeft: 4, color: '#8c8c8c' }} />
                                  </Tooltip>
                                  <Select
                                    style={{ width: '100%', marginTop: 4 }}
                                    value={shortcodeParams.format}
                                    onChange={(value) => updateShortcodeParam('format', value)}
                                    size="small"
                                    className="nodrag"
                                  >
                                    <Option value="auto">Auto-detect</Option>
                                    <Option value="text">Plain Text</Option>
                                    <Option value="html">HTML</Option>
                                    <Option value="json">JSON</Option>
                                    <Option value="markdown">Markdown</Option>
                                  </Select>
                                </div>

                                <div>
                                  <Text>Theme:</Text>
                                  <Tooltip title={parameterDescriptions.theme}>
                                    <InfoCircleOutlined style={{ marginLeft: 4, color: '#8c8c8c' }} />
                                  </Tooltip>
                                  <Select
                                    style={{ width: '100%', marginTop: 4 }}
                                    value={shortcodeParams.theme}
                                    onChange={(value) => updateShortcodeParam('theme', value)}
                                    size="small"
                                    className="nodrag"
                                  >
                                    <Option value="default">Default</Option>
                                    <Option value="dark">Dark</Option>
                                    <Option value="minimal">Minimal</Option>
                                  </Select>
                                </div>

                                <div>
                                  <Checkbox
                                    checked={shortcodeParams.show_timestamp}
                                    onChange={(e) => updateShortcodeParam('show_timestamp', e.target.checked)}
                                  >
                                    Show Timestamp
                                  </Checkbox>
                                  <Tooltip title={parameterDescriptions.show_timestamp}>
                                    <InfoCircleOutlined style={{ marginLeft: 4, color: '#8c8c8c' }} />
                                  </Tooltip>
                                </div>
                              </Space>
                            </Panel>

                            {/* Session Settings */}
                            <Panel header="Session Management" key="session">
                              <Space direction="vertical" style={{ width: '100%' }}>
                                <div>
                                  <Text>Session Type:</Text>
                                  <Tooltip title={parameterDescriptions.session_type}>
                                    <InfoCircleOutlined style={{ marginLeft: 4, color: '#8c8c8c' }} />
                                  </Tooltip>
                                  <Select
                                    style={{ width: '100%', marginTop: 4 }}
                                    value={shortcodeParams.session_type}
                                    onChange={(value) => updateShortcodeParam('session_type', value)}
                                    size="small"
                                    className="nodrag"
                                  >
                                    <Option value="auto">Automatic</Option>
                                    <Option value="public">Public (Shared)</Option>
                                    <Option value="private">Private (Per User)</Option>
                                    <Option value="dedicated">Dedicated (Isolated)</Option>
                                  </Select>
                                </div>

                                <div>
                                  <Text>Custom Session ID:</Text>
                                  <Tooltip title={parameterDescriptions.session_id}>
                                    <InfoCircleOutlined style={{ marginLeft: 4, color: '#8c8c8c' }} />
                                  </Tooltip>
                                  <Input
                                    value={shortcodeParams.session_id}
                                    onChange={(e) => updateShortcodeParam('session_id', e.target.value)}
                                    placeholder="Leave empty for automatic"
                                    size="small"
                                    style={{ marginTop: 4 }}
                                  />
                                </div>
                              </Space>
                            </Panel>

                            {/* Behavior Settings */}
                            <Panel header="Behavior Settings" key="behavior">
                              <Space direction="vertical" style={{ width: '100%' }}>
                                <div>
                                  <Text>Refresh Interval (ms):</Text>
                                  <Tooltip title={parameterDescriptions.refresh_interval}>
                                    <InfoCircleOutlined style={{ marginLeft: 4, color: '#8c8c8c' }} />
                                  </Tooltip>
                                  <InputNumber
                                    style={{ width: '100%', marginTop: 4 }}
                                    value={shortcodeParams.refresh_interval}
                                    onChange={(value) => updateShortcodeParam('refresh_interval', value)}
                                    min={1000}
                                    max={60000}
                                    step={1000}
                                    size="small"
                                  />
                                </div>

                                <div>
                                  <Text>Stale After (seconds):</Text>
                                  <Tooltip title={parameterDescriptions.stale_after}>
                                    <InfoCircleOutlined style={{ marginLeft: 4, color: '#8c8c8c' }} />
                                  </Tooltip>
                                  <InputNumber
                                    style={{ width: '100%', marginTop: 4 }}
                                    value={shortcodeParams.stale_after}
                                    onChange={(value) => updateShortcodeParam('stale_after', value)}
                                    min={5}
                                    max={300}
                                    size="small"
                                  />
                                </div>

                                <div>
                                  <Text>Error Display:</Text>
                                  <Tooltip title={parameterDescriptions.error_display}>
                                    <InfoCircleOutlined style={{ marginLeft: 4, color: '#8c8c8c' }} />
                                  </Tooltip>
                                  <Select
                                    style={{ width: '100%', marginTop: 4 }}
                                    value={shortcodeParams.error_display}
                                    onChange={(value) => updateShortcodeParam('error_display', value)}
                                    size="small"
                                    className="nodrag"
                                  >
                                    <Option value="inline">Show Inline</Option>
                                    <Option value="hidden">Hide Errors</Option>
                                    <Option value="modal">Show in Modal</Option>
                                  </Select>
                                </div>

                                <div>
                                  <Checkbox
                                    checked={shortcodeParams.stop_on_success}
                                    onChange={(e) => updateShortcodeParam('stop_on_success', e.target.checked)}
                                  >
                                    Stop on Success
                                  </Checkbox>
                                  <Tooltip title={parameterDescriptions.stop_on_success}>
                                    <InfoCircleOutlined style={{ marginLeft: 4, color: '#8c8c8c' }} />
                                  </Tooltip>
                                </div>

                                <div>
                                  <Checkbox
                                    checked={shortcodeParams.clear_on_refresh}
                                    onChange={(e) => updateShortcodeParam('clear_on_refresh', e.target.checked)}
                                  >
                                    Clear on Page Refresh
                                  </Checkbox>
                                  <Tooltip title={parameterDescriptions.clear_on_refresh}>
                                    <InfoCircleOutlined style={{ marginLeft: 4, color: '#8c8c8c' }} />
                                  </Tooltip>
                                </div>
                              </Space>
                            </Panel>
                          </Collapse>

                          {/* Generated shortcode with parameters */}
                          <div style={{ marginTop: 16 }}>
                            <Text strong>Configured Shortcode:</Text>
                            <Input.TextArea
                              value={generateShortcode()}
                              readOnly
                              style={{ marginTop: 8, marginBottom: 8 }}
                              autoSize={{ minRows: 2, maxRows: 5 }}
                            />
                            <Button 
                              onClick={() => {
                                navigator.clipboard.writeText(generateShortcode());
                                message.success('Configured shortcode copied!');
                              }}
                              icon={<CopyOutlined />}
                              type="primary"
                              size="small"
                              block
                            >
                              Copy Configured Shortcode
                            </Button>
                          </div>
                        </div>
                      )}

                      <Alert
                        message="How to use"
                        description="Paste this shortcode into any WordPress page, post, or widget where you want the output to appear."
                        type="info"
                        showIcon
                        style={{ marginTop: 12 }}
                        size="small"
                      />
                    </>
                  ) : (
                    <Alert
                      message="Save Workflow First"
                      description="The shortcode will be generated after you save the workflow."
                      type="warning"
                      showIcon
                    />
                  )}
                </div>
              )}

              {/* Database Output Type Content */}
              {outputType === 'database' && (
                <div>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Select
                      style={{ width: '100%' }}
                      value={selectedTable || (isNewTable ? 'Create New Table' : undefined)}
                      onChange={handleTableSelection}
                      placeholder="Select a table or create new"
                      className="nodrag"
                    >
                      {existingTables.map(table => (
                        <Option key={table} value={table}>{table}</Option>
                      ))}
                    </Select>
  
                    {isNewTable && (
                      <Input
                        placeholder="Enter new table name"
                        value={newTableName}
                        onChange={(e) => setNewTableName(e.target.value)}
                        className="nodrag"
                      />
                    )}
  
                    <Table
                      dataSource={columns}
                      columns={[
                        { title: 'Name', dataIndex: 'name', key: 'name', width: '30%' },
                        { title: 'Type', dataIndex: 'type', key: 'type', width: '20%' },
                        {
                          title: 'Mapping', 
                          dataIndex: 'mapping', 
                          key: 'mapping',
                          width: '40%',
                          render: (text, record) => (
                            <Space direction="vertical" style={{ width: '100%', margin: 0 }}>
                              <Input
                                value={text}
                                onChange={(e) => handleUnifiedMapping(record.name, e.target.value, 'save')}
                                placeholder="Select input or enter value"
                                className="nodrag"
                              />
                              <NodeInputHandler
                                nodeId={id}
                                onInsert={(tag) => handleUnifiedMapping(record.name, tag, 'save')}
                                showInputTag={true}
                              />
                            </Space>
                          )
                        },
                        {
                          title: 'Action',
                          key: 'action',
                          width: '10%',
                          render: (_, record, index) => (
                            isNewTable && (
                              <Button 
                                onClick={() => handleColumnDelete(index)} 
                                icon={<DeleteOutlined />} 
                              />
                            )
                          ),
                        },
                      ]}
                      pagination={false}
                      size="small"
                    />
  
                    {isNewTable && (
                      <Space style={{ marginTop: 8 }}>
                        <Button 
                          icon={<PlusOutlined />}
                          onClick={() => setIsColumnModalVisible(true)}
                        >
                          Add Column
                        </Button>
                        {columns.length > 0 && (
                          <Button 
                            type="primary" 
                            onClick={handleCreateNewTable}
                          >
                            Create Table
                          </Button>
                        )}
                      </Space>
                    )}
                  </Space>
                </div>
              )}

              {/* Webhook Output Type Content */}
              {outputType === 'webhook' && (
                <div>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <Space align="center" style={{ marginBottom: 4, width: '100%' }}>
                        <Text strong>Webhook URL</Text>
                        <Button 
                          type="link" 
                          size="small" 
                          onClick={() => setIsExplanationModalVisible(true)}
                          icon={<InfoCircleOutlined />}
                        >
                          How it works
                        </Button>
                      </Space>
                      <Input
                        placeholder="https://example.com/webhook"
                        value={webhookUrl}
                        onChange={handleWebhookUrlChange}
                        style={{ marginBottom: 8 }}
                        className="nodrag"
                      />
                      <Button 
                        onClick={handleTestWebhook} 
                        loading={isTestingWebhook}
                        style={{ marginBottom: 16 }}
                      >
                        Test Webhook
                      </Button>
                    </div>
                    
                    <div>
                      <Text strong>Data to Send</Text>
                      <Table
                        dataSource={webhookKeys}
                        columns={[
                          {
                            title: 'Key', 
                            dataIndex: 'key', 
                            key: 'key',
                            width: '30%'
                          },
                          {
                            title: 'Value',
                            dataIndex: 'mapping',
                            key: 'mapping',
                            width: '60%',
                            render: (text, record) => (
                              <Space direction="vertical" style={{ width: '100%', margin: 0 }}>
                                <Input
                                  value={text}
                                  onChange={(e) => handleUnifiedMapping(record.key, e.target.value, 'webhook')}
                                  placeholder="Select input or enter value"
                                  className="nodrag"
                                />
                                <NodeInputHandler
                                  nodeId={id}
                                  onInsert={(tag) => handleUnifiedMapping(record.key, tag, 'webhook')}
                                  showInputTag={true}
                                />
                              </Space>
                            )
                          },
                          {
                            title: 'Action',
                            key: 'action',
                            width: '10%',
                            render: (_, record) => (
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />} 
                                onClick={() => handleWebhookKeyDelete(record.key)} 
                              />
                            ),
                          },
                        ]}
                        pagination={false}
                        size="small"
                      />
                      <Button 
                        icon={<PlusOutlined />} 
                        onClick={() => setIsWebhookKeyModalVisible(true)}
                        style={{ marginTop: 8 }}
                      >
                        Add Key
                      </Button>
                    </div>
                  </Space>
                </div>
              )}

              {/* Google Sheets Output Type Content */}
              {outputType === 'sheet' && (
                <div>
                  {renderGoogleSheetsOutput()}
                </div>
              )}

              {/* Google Drive Output Type Content */}
              {outputType === 'document' && (
                <div>
                  {renderGoogleDriveOutput()}
                </div>
              )}
            </Space>
          </TabPane>

          {delayEnabled && (
            <TabPane tab="Delay" key="delay">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Alert
                  message="Output Delay"
                  description="The output will be delayed by the specified duration."
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                  action={
                    <Button 
                      danger 
                      size="small" 
                      onClick={() => {
                        setDelayEnabled(false);
                        setActiveTab('output');
                      }}
                    >
                      Disable Delay
                    </Button>
                  }
                />

                <div style={{ 
                  background: '#f5f5f5', 
                  padding: 12, 
                  borderRadius: 6 
                }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <Text strong>Delay Duration</Text>
                      <Space style={{ width: '100%', marginTop: 8 }}>
                        <InputNumber
                          style={{ width: '60%' }}
                          value={delayValue}
                          onChange={setDelayValue}
                          min={1}
                          className="nodrag"
                        />
                        <Select
                          style={{ width: '40%' }}
                          value={delayUnit}
                          onChange={setDelayUnit}
                          className="nodrag"
                        >
                          <Option value="seconds">Seconds</Option>
                          <Option value="minutes">Minutes</Option>
                          <Option value="hours">Hours</Option>
                        </Select>
                      </Space>
                    </div>
                    
                    <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
                      The workflow will pause for {delayValue} {delayUnit} before continuing to the next node.
                    </Paragraph>
                  </Space>
                </div>
              </Space>
            </TabPane>
          )}

          <TabPane tab="Email" key="email">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Alert
                message="Email Notification"
                description="Send the output via email when the workflow completes."
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />

              <div style={{ 
                background: '#f5f5f5', 
                padding: 12, 
                borderRadius: 6 
              }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div>
                    <Text strong>To:</Text>
                    <Input
                      placeholder="recipient@example.com"
                      value={email.to}
                      onChange={(e) => setEmail({ ...email, to: e.target.value })}
                      style={{ marginTop: 4 }}
                      className="nodrag"
                    />
                  </div>

                  <div>
                    <Text strong>Subject:</Text>
                    <NodeInputHandler
                      nodeId={id}
                      value={email.subject}
                      onChange={(newData) => setEmail({ ...email, subject: newData.content })}
                      placeholder="Email subject"
                      nodes={nodes}
                    />
                  </div>

                  <div>
                    <Text strong>Body:</Text>
                    <NodeInputHandler
                      nodeId={id}
                      value={email.body}
                      onChange={(newData) => setEmail({ ...email, body: newData.content })}
                      placeholder="Email body content"
                      nodes={nodes}
                      multiline={true}
                    />
                  </div>

                  <Button 
                    onClick={handleEmailSend} 
                    loading={isSendingEmail}
                    icon={<SendOutlined />}
                    type="primary"
                  >
                    Send Test Email
                  </Button>
                </Space>
              </div>
            </Space>
          </TabPane>
        </Tabs>
      </Card>

      {/* Column Modal */}
      <Modal
        title="Add Column"
        visible={isColumnModalVisible}
        onCancel={() => setIsColumnModalVisible(false)}
        footer={null}
      >
        <Form
          onFinish={handleColumnAdd}
          layout="vertical"
        >
          <Form.Item
            name="name"
            label="Column Name"
            rules={[{ required: true, message: 'Please input column name!' }]}
          >
            <Input placeholder="e.g., user_name" />
          </Form.Item>
          <Form.Item
            name="type"
            label="Column Type"
            rules={[{ required: true, message: 'Please select column type!' }]}
          >
            <Select>
              <Option value="text">Text</Option>
              <Option value="number">Number</Option>
              <Option value="date">Date</Option>
              <Option value="boolean">Boolean</Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              Add Column
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Webhook Key Modal */}
      <Modal
        title="Add Webhook Key"
        visible={isWebhookKeyModalVisible}
        onCancel={() => {
          setIsWebhookKeyModalVisible(false);
          setNewWebhookKey('');
        }}
        onOk={handleWebhookKeyAdd}
      >
        <Input
          placeholder="Enter key name (e.g., user_email)"
          value={newWebhookKey}
          onChange={(e) => setNewWebhookKey(e.target.value)}
        />
      </Modal>

      {/* Webhook Explanation Modal */}
      <Modal
        title="How Webhook Output Works"
        visible={isExplanationModalVisible}
        onCancel={() => setIsExplanationModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsExplanationModalVisible(false)}>
            Got it!
          </Button>
        ]}
        width={600}
      >
        <Typography>
          <Paragraph>
            The Webhook output type allows you to send workflow results to an external URL using HTTP POST requests.
            Here's how it works:
          </Paragraph>
          <ol>
            <li>Define the webhook URL where you want to send the data.</li>
            <li>Create key/value pairs to structure your data.</li>
            <li>Map outputs from previous nodes to these keys.</li>
            <li>When the workflow reaches this node, it will send a POST request to the specified URL with the mapped data.</li>
          </ol>
          <Paragraph>
            <strong>Key Structure:</strong>
          </Paragraph>
          <ul>
            <li>Use forward slashes (/) to create nested objects. For example, "Data/Name/First" will create: {`{ "Data": { "Name": { "First": "value" } } }`}</li>
            <li>To create arrays, use numeric indices. For example, "Data/Items/0" and "Data/Items/1" will create: {`{ "Data": { "Items": ["value1", "value2"] } }`}</li>
          </ul>
        </Typography>
      </Modal>
    </>
  );
};

export default OutputNode;